"use client";

import { useClientTranslations } from "@/lib/translate";
import { ApplicantStatus } from "@/modules/types/enum";
import {
  CancelCircleIcon,
  CheckmarkCircle01Icon,
  Clock04Icon,
  EyeIcon,
  CircleIcon,
  DeadIcon,
  OfficeIcon,
  UserCheck01Icon,
  HeartAddIcon,
  ArrowDown01Icon,
  Message01Icon,
} from "hugeicons-react";
import React, { useState } from "react";
import { useStatusJobApplyChange } from "@/modules/hooks/employee/useCandidateJob";

const statusMap: Record<
  ApplicantStatus,
  {
    key: ApplicantStatus;
    labelKey: string;
    descKey: string;
    color: string;
    icon: React.ReactNode;
  }
> = {
  [ApplicantStatus.pending]: {
    key: ApplicantStatus.pending,
    labelKey: "label.pending",
    descKey: "desc.pending",
    color:
      "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200",
    icon: (
      <Clock04Icon className="w-5 h-5 flex-shrink-0 text-yellow-500 dark:text-yellow-400" />
    ),
  },
  [ApplicantStatus.seen]: {
    key: ApplicantStatus.seen,
    labelKey: "label.seen",
    descKey: "desc.seen",
    color: "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200",
    icon: (
      <EyeIcon className="w-5 h-5 flex-shrink-0 text-blue-500 dark:text-blue-400" />
    ),
  },
  [ApplicantStatus.considering]: {
    key: ApplicantStatus.considering,
    labelKey: "label.considering",
    descKey: "desc.considering",
    color:
      "bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200",
    icon: (
      <CircleIcon className="w-5 h-5 flex-shrink-0 text-indigo-500 dark:text-indigo-400" />
    ),
  },
  [ApplicantStatus.accepted]: {
    key: ApplicantStatus.accepted,
    labelKey: "label.accepted",
    descKey: "desc.accepted",
    color: "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200",
    icon: (
      <CheckmarkCircle01Icon className="w-5 h-5 flex-shrink-0 text-green-500 dark:text-green-400" />
    ),
  },
  [ApplicantStatus.rejected]: {
    key: ApplicantStatus.rejected,
    labelKey: "label.rejected",
    descKey: "desc.rejected",
    color: "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200",
    icon: (
      <CancelCircleIcon className="w-5 h-5 flex-shrink-0 text-red-500 dark:text-red-400" />
    ),
  },
  [ApplicantStatus.exchange]: {
    key: ApplicantStatus.exchange,
    labelKey: "label.exchange",
    descKey: "desc.exchange",
    color:
      "bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200",
    icon: (
      <Message01Icon className="w-5 h-5 flex-shrink-0 text-orange-500 dark:text-orange-400" />
    ),
  },
  [ApplicantStatus.canceled]: {
    key: ApplicantStatus.canceled,
    labelKey: "label.canceled",
    descKey: "desc.canceled",
    color: "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300",
    icon: (
      <CancelCircleIcon className="w-5 h-5 flex-shrink-0 text-gray-500 dark:text-gray-400" />
    ),
  },
  [ApplicantStatus.denied]: {
    key: ApplicantStatus.denied,
    labelKey: "label.denied",
    descKey: "desc.denied",
    color: "bg-red-200 dark:bg-red-800 text-red-700 dark:text-red-300",
    icon: (
      <DeadIcon className="w-5 h-5 flex-shrink-0 text-red-400 dark:text-red-300" />
    ),
  },
  [ApplicantStatus.offered]: {
    key: ApplicantStatus.offered,
    labelKey: "label.offered",
    descKey: "desc.offered",
    color: "bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200",
    icon: (
      <OfficeIcon className="w-5 h-5 flex-shrink-0 text-teal-500 dark:text-teal-400" />
    ),
  },
  [ApplicantStatus.hired]: {
    key: ApplicantStatus.hired,
    labelKey: "label.hired",
    descKey: "desc.hired",
    color: "bg-green-200 dark:bg-green-800 text-green-900 dark:text-green-100",
    icon: (
      <UserCheck01Icon className="w-5 h-5 flex-shrink-0 text-green-700 dark:text-green-300" />
    ),
  },
};

interface ActionButton {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  loading?: boolean;
}

export default function JobApplyStatusBadge({
  jobApplyId,
  status,
  small,
  isSaved = false,
  forceUpdate = false,
}: {
  jobApplyId: string;
  status: ApplicantStatus;
  small?: boolean;
  isSaved?: boolean;
  forceUpdate?: boolean;
}) {
  const { t } = useClientTranslations("job-apply");
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const [localStatus, setLocalStatus] = useState<ApplicantStatus>(status);

  // Update local status when prop changes
  React.useEffect(() => {
    setLocalStatus(status);
  }, [status]);

  // Integrate the status change hook
  const { loading, acceptExchange, cancelExchange, denyOffer, acceptOffer } =
    useStatusJobApplyChange();

  // Helper to update status after API success if forceUpdate is true
  const updateStatusAfterApi = (newStatus: ApplicantStatus) => {
    if (forceUpdate) {
      setLocalStatus(newStatus);
    }
  };

  const getAvailableActions = (): ActionButton[] => {
    switch (localStatus) {
      case ApplicantStatus.accepted:
        return [
          {
            key: "exchange",
            label: t("action.exchange"),
            icon: <Message01Icon className="w-4 h-4" />,
            onClick: async () => {
              await acceptExchange(jobApplyId, status);
              updateStatusAfterApi(ApplicantStatus.exchange);
              setIsActionMenuOpen(false);
            },
            loading: loading === "acceptExchange",
          },
          {
            key: "cancel",
            label: t("action.cancel"),
            icon: <CancelCircleIcon className="w-4 h-4" />,
            onClick: async () => {
              await cancelExchange(jobApplyId, status);
              updateStatusAfterApi(ApplicantStatus.canceled);
              setIsActionMenuOpen(false);
            },
            loading: loading === "cancelExchange",
          },
        ];
      case ApplicantStatus.offered:
        return [
          {
            key: "deny",
            label: t("action.deny"),
            icon: <DeadIcon className="w-4 h-4" />,
            onClick: async () => {
              await denyOffer(jobApplyId, status);
              updateStatusAfterApi(ApplicantStatus.denied);
              setIsActionMenuOpen(false);
            },
            loading: loading === "denyOffer",
          },
          {
            key: "hire",
            label: t("action.hire"),
            icon: <CheckmarkCircle01Icon className="w-4 h-4" />,
            onClick: async () => {
              await acceptOffer(jobApplyId, status);
              updateStatusAfterApi(ApplicantStatus.hired);
              setIsActionMenuOpen(false);
            },
            loading: loading === "acceptOffer",
          },
        ];
      default:
        return [];
    }
  };

  const availableActions = getAvailableActions();
  const hasActions = availableActions.length > 0;

  // Handle saved jobs with special status
  if (isSaved) {
    const savedStatusInfo = {
      key: "saved" as any,
      labelKey: "label.saved",
      descKey: "desc.saved",
      color: "bg-pink-100 dark:bg-pink-900 text-pink-800 dark:text-pink-200",
      icon: (
        <HeartAddIcon className="w-5 h-5 flex-shrink-0 text-pink-500 dark:text-pink-400" />
      ),
    };

    const badge = (
      <div
        className={`flex items-center gap-3 px-3 py-1 rounded-lg shadow-sm ${savedStatusInfo.color}`}
      >
        {savedStatusInfo.icon}
        <div>
          <div className="font-semibold text-xs sm:text-sm md:text-base">
            {t(savedStatusInfo.labelKey)}
          </div>
          <div className="text-xs">{t(savedStatusInfo.descKey)}</div>
        </div>
      </div>
    );

    const smallBadge = (
      <div
        className={`flex items-center gap-3 px-3 py-1 w-fit rounded-lg shadow-sm ${savedStatusInfo.color}`}
      >
        {savedStatusInfo.icon}
        <div className="text-xs">{t(savedStatusInfo.labelKey)}</div>
      </div>
    );

    return small ? smallBadge : badge;
  }

  const statusInfo = statusMap[localStatus] ?? {
    key: ApplicantStatus,
    labelKey: "label.unknown",
    descKey: "desc.unknown",
    color: "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200",
    icon: (
      <CircleIcon className="w-5 h-5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
    ),
  };

  const badge = (
    <div className="flex">
      <div
        className={`w-fit flex items-center gap-3 px-3 py-1 rounded-l-lg ${
          statusInfo.color
        } ${""} ${hasActions ? "rounded-r-none" : "rounded-r-lg"}`}
      >
        {statusInfo.icon}
        <div>
          <div className="font-semibold text-xs sm:text-sm md:text-base w-fit">
            {t(statusInfo.labelKey)}
          </div>
          <div className="max-w-full text-xs text-wrap">
            {t(statusInfo.descKey)}
          </div>
        </div>
      </div>

      {/* Action Button */}
      {hasActions && (
        <div className="relative flex">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsActionMenuOpen(!isActionMenuOpen);
            }}
            className={`px-3 rounded-r-lg border-l border-current/20 ${statusInfo.color} hover:brightness-95 dark:hover:brightness-110 transition flex items-center justify-center min-h-full`}
          >
            <ArrowDown01Icon className="w-4 h-4 fill-current" />
          </button>

          {/* Action Dropdown Menu */}
          {isActionMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setIsActionMenuOpen(false)}
              />
              <div className="absolute min-w-60 md:min-w-72 right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                {availableActions.map((action) => (
                  <button
                    key={action.key}
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      await action.onClick();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                    disabled={!!action.loading}
                  >
                    {action.icon}
                    {action.label}
                    {action.loading && (
                      <span className="ml-2 animate-spin">...</span>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );

  const smallBadge = (
    <div className="flex">
      <div
        className={`flex items-center gap-3 px-3 py-1 w-fit rounded-l-lg shadow-sm ${statusInfo.color}`}
      >
        {statusInfo.icon}
        <div className="text-xs">{t(statusInfo.labelKey)}</div>
      </div>

      {/* Action Button for small badge */}
      {hasActions && (
        <div className="relative flex">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsActionMenuOpen(!isActionMenuOpen);
            }}
            className={`px-3 rounded-r-lg shadow-sm border-l border-current/20 ${statusInfo.color} flex items-center justify-center min-h-full`}
          >
            <ArrowDown01Icon className="w-4 h-4 fill-current" />
          </button>

          {/* Action Dropdown Menu */}
          {isActionMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setIsActionMenuOpen(false)}
              />
              <div className="absolute min-w-60 md:min-w-72 right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                {availableActions.map((action) => (
                  <button
                    key={action.key}
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      await action.onClick();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                    disabled={!!action.loading}
                  >
                    {action.icon}
                    {action.label}
                    {action.loading && (
                      <span className="ml-2 animate-spin">...</span>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );

  return small ? smallBadge : badge;
}
